import { FunctionComponent, ReactNode, useState } from "react";
import {
  LabelPairedCircleInfoSmRegularIcon,
  LabelPairedLoaderCaptionRegularIcon,
  LabelPairedLoaderLgRegularIcon,
  LabelPairedLoaderMdRegularIcon,
  LabelPairedLoaderSmRegularIcon,
} from "@deriv/quill-icons/LabelPaired";
import { useFocusHandling } from "../../../hooks/useFocusHandling";
import ButtonBlackStyles from "../base/styles/basic/BasicButtonBlack.module.css";
import ButtonWhiteStyles from "../base/styles/basic/BasicButtonWhite.module.css";
import ButtonCoralStyles from "../base/styles/basic/BasicButtonCoral.module.css";
import ButtonBuyStyles from "../base/styles/basic/BasicButtonBuy.module.css";
import ButtonSellStyles from "../base/styles/basic/BasicButtonSell.module.css";
import ButtonBlackWhiteStyles from "../base/styles/basic/BasicButtonBlackWhite.module.css";
import ButtonWhiteBlackStyles from "../base/styles/basic/BasicButtonWhiteBlack.module.css";
import ButtonGlacierStyles from "../base/styles/basic/BasicButtonGlacier.module.css";
import IconButtonBlackStyles from "../base/styles/icon/IconButtonBlack.module.css";
import IconButtonWhiteStyles from "../base/styles/icon/IconButtonWhite.module.css";
import IconButtonCoralStyles from "../base/styles/icon/IconButtonCoral.module.css";
import IconButtonBlackWhiteStyles from "../base/styles/icon/IconButtonBlackWhite.module.css";
import IconButtonWhiteBlackStyles from "../base/styles/icon/IconButtonWhiteBlack.module.css";

export type BaseButtonType = {
  className?: string;
  label?: string;
  hasIconLeft?: boolean;
  hasIconRight?: boolean;
  hasLabel?: boolean;
  iconLoading?: boolean;
  showFocusRing?: boolean;

  /** Variant props */
  size?: "lg" | "xl" | "md" | "sm";
  state?: "default" | "loading" | "disabled";
  style?: "primary" | "secondary" | "tertiary";
  width?: "hug content" | "fill container";

  /** Custom Props  */
  color?:
    | "coral"
    | "black"
    | "white"
    | "blackWhite"
    | "whiteBlack"
    | "buy"
    | "sell"
    | "glacier";
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  icon?: ReactNode;
  type?: "basic" | "icon";
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

const buttonStyles = {
  black: ButtonBlackStyles,
  white: ButtonWhiteStyles,
  coral: ButtonCoralStyles,
  buy: ButtonBuyStyles,
  sell: ButtonSellStyles,
  blackWhite: ButtonBlackWhiteStyles,
  whiteBlack: ButtonWhiteBlackStyles,
  glacier: ButtonGlacierStyles,
};

const iconButtonStyles = {
  black: IconButtonBlackStyles,
  white: IconButtonWhiteStyles,
  coral: IconButtonCoralStyles,
  blackWhite: IconButtonBlackWhiteStyles,
  whiteBlack: IconButtonWhiteBlackStyles,
};

const BaseButton: FunctionComponent<BaseButtonType> = ({
  className = "",
  size = "lg",
  state = "default",
  style = "primary",
  width = "hug content",
  color = "coral",
  label = "Label",
  showFocusRing = false,
  iconLeft,
  iconRight,
  icon = <LabelPairedCircleInfoSmRegularIcon />,
  onClick,
  type = "basic",
}) => {
  const styles =
    type === "basic"
      ? buttonStyles[color as keyof typeof buttonStyles]
      : iconButtonStyles[color as keyof typeof iconButtonStyles];

  const buttonId = `quill-button-${type}-${state}-${style}-${type}-${size}-${color}`;

  const loaderIcons = {
    sm: LabelPairedLoaderCaptionRegularIcon,
    md: LabelPairedLoaderSmRegularIcon,
    lg: LabelPairedLoaderMdRegularIcon,
    xl: LabelPairedLoaderLgRegularIcon,
  };

  const [highlight, setHighlight] = useState(showFocusRing);

  const { handleFocus, handleBlur } = useFocusHandling({
    isDisabled: state === "disabled",
    setHighlight,
  });

  const renderContent = () => {
    if (state === "loading") {
      const LoaderIcon = loaderIcons[size];
      return (
        <div
          className={[styles.iconLoading, "quill-ui-next-spinner"].join(" ")}
        >
          {/* Empty label div required for icon color inheritance */}
          <div className={styles.label}></div>
          <div className={styles.iconColorReference}></div>
          {LoaderIcon && <LoaderIcon />}
        </div>
      );
    }

    return type === "icon" ? (
      <>
        <div className={styles.iconCustom}>
          <div className={styles.iconColorReference}></div>
          {icon}
        </div>
      </>
    ) : (
      <>
        <div className={styles.iconLeft}>{iconLeft}</div>
        {label && <div className={styles.label}>{label}</div>}
        <div className={styles.iconRight}>{iconRight}</div>
      </>
    );
  };

  return (
    <button
      className={[styles.root, className, "quill-ui-next-component"].join(" ")}
      data-id={buttonId}
      data-size={size}
      data-state={state}
      disabled={state === "disabled"}
      data-style={style}
      data-width={width}
      data-type={type}
      tabIndex={state === "loading" ? -1 : 0}
      onClick={(e) => state !== "loading" && onClick?.(e)}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {renderContent()}
      {highlight && <div className={styles.bgFocus} />}
    </button>
  );
};

export default BaseButton;
